import type { IAny } from '@sixui/ui';
import type {
  GeoJSONSource,
  IControl,
  MapEvent,
  MapMouseEvent,
} from 'mapbox-gl';
import type { ViewStateChangeEvent } from 'react-map-gl';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { useMergeRefs } from '@floating-ui/react';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import { getTextContrastColor, useColorScheme } from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import circle from '@turf/circle';
import mapboxgl from 'mapbox-gl';
import { useTranslation } from 'react-i18next';
import Map, {
  FullscreenControl,
  GeolocateControl,
  Layer,
  MapRef,
  NavigationControl,
  Source,
} from 'react-map-gl';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiCrowdGeoSearchRequestBody } from '~/lib/api/modules/Crowd';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';
import { getAppSettings } from '~/lib/appSettings';
import {
  clusterCountLayer,
  clusterLayer,
  searchZoneLayer,
  unclusteredPointLayer,
} from './clusters';

import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';

const CUSTOMIZE_COLORS = false;
const INITIAL_VIEW_STATE = {
  longitude: 0,
  latitude: 18.347270325583793,
  zoom: 0,
};
const BBOX_DURATION = 300;
const DISPLAY_SEARCH_ZONE = false;

export type ICommunityMapProps = IContainerProps & {
  appId: string;
  children?: React.ReactNode;
  data?: GeoJSON.GeoJSON;
  onMove?: (event: ViewStateChangeEvent) => void;
  filter?: IApiCrowdGeoSearchRequestBody;
};

const styles = stylex.create({
  host: {
    position: 'relative',
    overflow: 'hidden',
  },
});

export const CommunityMap = forwardRef<MapRef, ICommunityMapProps>(
  function CommunityMap(props, forwardedRef) {
    const { sx, appId, children, data, onMove, filter } = props;
    const appSettings = getAppSettings();
    const colorScheme = useColorScheme();
    const appQuery = useAppQuery({ appId });
    const primaryColor =
      appQuery.data?.settings?.press?.env?.appThemeColorPrimary ||
      appSettings.theme.appDefaultPrimaryColor;
    const onPrimaryColor = getTextContrastColor(primaryColor);
    const { t, i18n } = useTranslation();
    const mapRef = useRef<MapRef>(null);
    const map = mapRef.current;
    const [mapLoaded, setMapLoaded] = useState(false);
    const [searchZone, setSearchZone] = useState<GeoJSON.Feature>();

    // Update the search zone when the filter changes.
    useEffect(() => {
      setSearchZone(
        typeof filter?.lat === 'number' &&
          typeof filter?.lng === 'number' &&
          typeof filter?.radius === 'number' &&
          filter?.radius > 0
          ? circle([filter.lng, filter.lat], filter.radius, {
              units: 'meters',
            })
          : undefined,
      );
    }, [filter?.lat, filter?.lng, filter?.radius]);

    // Fit the map when the data change.
    useEffect(() => {
      if (!map || !mapLoaded) {
        return;
      }

      const hasGeoFilter = !!(
        typeof filter?.lat === 'number' &&
        typeof filter?.lng === 'number' &&
        (filter?.radius ?? 0) > 0
      );

      if (!hasGeoFilter) {
        map.easeTo({
          center: [INITIAL_VIEW_STATE.longitude, INITIAL_VIEW_STATE.latitude],
          zoom: INITIAL_VIEW_STATE.zoom,
          duration: BBOX_DURATION,
        });
      }
    }, [map, mapLoaded, data, filter?.lat, filter?.lng, filter?.radius]);

    const handleMapLoaded = (event: MapEvent): void => {
      setMapLoaded(true);

      const map = event.target;
      map
        .getStyle()
        ?.layers.filter((layer) => layer.type === 'symbol')
        .forEach((layer) => {
          if (CUSTOMIZE_COLORS) {
            map.setPaintProperty(layer.id, 'text-color', primaryColor);
          }
          map.setLayoutProperty(layer.id, 'text-field', [
            'coalesce',
            ['get', `name_${i18n.language}`],
            ['get', 'name'],
          ]);
        });
      map.addControl(
        new MapboxGeocoder({
          accessToken: appSettings.mapBox.accessToken,
          placeholder: t('SearchCommunity.searchLocation'),
          clearAndBlurOnEsc: true,
          clearOnBlur: true,
          mapboxgl: mapboxgl as IAny,
        }) as unknown as IControl,
        'bottom-right',
      );
    };

    const handleMapClick = (event: MapMouseEvent): void => {
      if (!map || !mapLoaded) {
        return;
      }

      const feature = event.features?.[0];
      const clusterId = feature?.properties?.cluster_id;
      if (!clusterId) {
        return;
      }

      const mapboxSource = map.getSource('users');
      if (!mapboxSource) {
        return;
      }

      (mapboxSource as GeoJSONSource).getClusterExpansionZoom(
        clusterId as number,
        (err, zoom) => {
          if (err) {
            return;
          }

          if (feature.geometry.type !== 'Point') {
            return;
          }

          map.easeTo({
            center: [
              feature.geometry.coordinates[0],
              feature.geometry.coordinates[1],
            ],
            zoom: zoom ?? undefined,
            duration: 300,
          });
        },
      );
    };

    const mapHandleRef = useMergeRefs([mapRef, forwardedRef]);

    return (
      <div {...stylex.props([styles.host, sx])}>
        <Map
          mapLib={mapboxgl as IAny}
          mapboxAccessToken={appSettings.mapBox.accessToken}
          initialViewState={INITIAL_VIEW_STATE}
          style={{ borderRadius: 'inherit' }}
          mapStyle={appSettings.mapBox.styles[colorScheme.variant]}
          projection={{ name: 'mercator' }}
          renderWorldCopies={false}
          interactiveLayerIds={[clusterLayer.id!]}
          onLoad={handleMapLoaded}
          onClick={handleMapClick}
          attributionControl={false}
          onMove={onMove}
          ref={mapHandleRef}
        >
          <FullscreenControl />
          <GeolocateControl />
          <NavigationControl />

          {mapLoaded && (
            <>
              {children}

              {data && (
                <Source
                  id="users"
                  type="geojson"
                  data={data}
                  cluster
                  clusterMaxZoom={14}
                  clusterRadius={50}
                >
                  <Layer
                    {...clusterLayer}
                    type="circle"
                    paint={{
                      ...clusterLayer.paint,
                      'circle-color': primaryColor,
                    }}
                  />
                  <Layer
                    {...clusterCountLayer}
                    type="symbol"
                    paint={{
                      ...clusterCountLayer.paint,
                      'text-color': onPrimaryColor,
                    }}
                  />
                  <Layer
                    {...unclusteredPointLayer}
                    type="circle"
                    paint={{
                      'circle-color': primaryColor,
                    }}
                  />
                </Source>
              )}

              {searchZone && DISPLAY_SEARCH_ZONE && (
                <Source id="search-zone" type="geojson" data={searchZone}>
                  <Layer
                    {...searchZoneLayer}
                    type="line"
                    paint={{
                      ...searchZoneLayer.paint,
                      'line-color': primaryColor,
                    }}
                  />
                </Source>
              )}
            </>
          )}
        </Map>
      </div>
    );
  },
);
